import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Truck, PackageCheck, XCircle, CheckCircle2, Clock, DollarSign,
  RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Store, Loader2
} from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/business`;

/**
 * Tela do admin para operar o ciclo de vida do pedido: confirmar entrega,
 * cancelar pedido, e autorizar repasse ao fornecedor.
 *
 * Sem esta tela, a lógica de negócio (garantia reservada, repasse
 * autorizado por admin, confirmação de entrega) construída no backend
 * era inoperável na prática - existia e funcionava, mas ninguém tinha
 * como acioná-la pela interface.
 *
 * IMPORTANTE: usa authService.fetchAuth (que anexa o token de login) em
 * vez de fetch/axios sem autenticação, que é o padrão usado no resto do
 * frontend hoje. Isso é proposital - o resto do app precisa da mesma
 * correção, registrada separadamente (ver changelog).
 */

const formatarMoeda = (valor) =>
  (valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatarData = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const RepassesAdmin = () => {
  const [abaAtiva, setAbaAtiva] = useState("prontos"); // prontos | aguardando | historico
  const [carregando, setCarregando] = useState(true);
  const [processandoId, setProcessandoId] = useState(null);
  const [gruposRepasse, setGruposRepasse] = useState([]);
  const [pedidosAguardando, setPedidosAguardando] = useState([]);
  const [pedidosHistorico, setPedidosHistorico] = useState([]);
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [modalCancelar, setModalCancelar] = useState(null); // pedido a cancelar
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [modalRepasse, setModalRepasse] = useState(null); // grupo (fornecedor) a autorizar repasse

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const [resRepasses, resAguardando, resHistorico] = await Promise.all([
        authService.fetchAuth(`${API}/admin/repasses/pendentes`),
        authService.fetchAuth(`${API}/admin/pedidos/aguardando-entrega`),
        authService.fetchAuth(`${API}/admin/pedidos?limit=50`),
      ]);

      if (resRepasses.ok) setGruposRepasse(await resRepasses.json());
      if (resAguardando.ok) setPedidosAguardando(await resAguardando.json());
      if (resHistorico.ok) {
        const todos = await resHistorico.json();
        setPedidosHistorico(todos.filter((p) => p.status === "entregue" || p.status === "cancelado"));
      }
    } catch (error) {
      console.error("Erro ao carregar dados de repasses:", error);
      toast.error("Não foi possível carregar os dados. Verifique sua sessão e tente novamente.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const confirmarEntrega = async (pedidoId) => {
    setProcessandoId(pedidoId);
    try {
      const res = await authService.fetchAuth(`${API}/pedido/confirmar-entrega`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pedido_id: pedidoId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erro ao confirmar entrega");
      toast.success("Entrega confirmada. Pedido já está pronto para repasse.");
      await carregarDados();
    } catch (error) {
      toast.error(error.message || "Erro ao confirmar entrega");
    } finally {
      setProcessandoId(null);
    }
  };

  const abrirCancelamento = (pedido) => {
    setModalCancelar(pedido);
    setMotivoCancelamento("");
  };

  const confirmarCancelamento = async () => {
    if (!modalCancelar) return;
    setProcessandoId(modalCancelar.id);
    try {
      const res = await authService.fetchAuth(`${API}/pedido/cancelar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pedido_id: modalCancelar.id,
          motivo: motivoCancelamento || "Cancelado pelo admin",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erro ao cancelar pedido");
      toast.success("Pedido cancelado. Garantia liberada ao afiliado e fornecedor notificado para não enviar.");
      setModalCancelar(null);
      await carregarDados();
    } catch (error) {
      toast.error(error.message || "Erro ao cancelar pedido");
    } finally {
      setProcessandoId(null);
    }
  };

  const abrirConfirmacaoRepasse = (grupo) => {
    setModalRepasse(grupo);
  };

  const confirmarAutorizacaoRepasse = async () => {
    if (!modalRepasse) return;
    const grupo = modalRepasse;
    const pedidosIds = (grupo.pedidos || []).map((p) => p.id);
    setProcessandoId(grupo.fornecedor_id);
    try {
      const res = await authService.fetchAuth(`${API}/repasse/autorizar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pedidos_ids: pedidosIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erro ao autorizar repasse");
      toast.success(
        `Repasse de ${formatarMoeda(grupo.valor_total)} autorizado para ${grupo.fornecedor_nome}.`
      );
      setModalRepasse(null);
      await carregarDados();
    } catch (error) {
      toast.error(error.message || "Erro ao autorizar repasse");
    } finally {
      setProcessandoId(null);
    }
  };

  const alternarGrupo = (fornecedorId) => {
    setGruposExpandidos((prev) => ({ ...prev, [fornecedorId]: !prev[fornecedorId] }));
  };

  const totalPendenteGeral = gruposRepasse.reduce((soma, g) => soma + (g.valor_total || 0), 0);

  const abas = [
    { id: "prontos", label: "Prontos para repasse", icone: DollarSign, contagem: gruposRepasse.length },
    { id: "aguardando", label: "Aguardando entrega", icone: Truck, contagem: pedidosAguardando.length },
    { id: "historico", label: "Histórico recente", icone: Clock, contagem: pedidosHistorico.length },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PackageCheck className="w-6 h-6 text-emerald-600" />
            Repasses e Pedidos
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Confirme entregas, cancele pedidos e autorize repasses aos fornecedores.
            A garantia do afiliado só é liberada ao fornecedor depois que você autorizar aqui.
          </p>
        </div>
        <Button variant="outline" onClick={carregarDados} disabled={carregando}>
          <RefreshCw className={`w-4 h-4 mr-2 ${carregando ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {gruposRepasse.length > 0 && (
        <Card className="p-4 mb-6 bg-emerald-50 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700">Total pendente de repasse (todos os fornecedores)</p>
              <p className="text-2xl font-bold text-emerald-800">{formatarMoeda(totalPendenteGeral)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-emerald-400" />
          </div>
        </Card>
      )}

      {/* Abas */}
      <div className="flex gap-2 mb-6 border-b">
        {abas.map((aba) => {
          const Icone = aba.icone;
          return (
            <button
              key={aba.id}
              onClick={() => setAbaAtiva(aba.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                abaAtiva === aba.id
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-gray-400 hover:text-foreground"
              }`}
            >
              <Icone className="w-4 h-4" />
              {aba.label}
              {aba.contagem > 0 && (
                <span className="bg-muted text-xs px-1.5 py-0.5 rounded-full">{aba.contagem}</span>
              )}
            </button>
          );
        })}
      </div>

      {carregando && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      )}

      {/* ABA: Prontos para repasse */}
      {!carregando && abaAtiva === "prontos" && (
        <div className="space-y-4">
          {gruposRepasse.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum repasse pendente no momento. Tudo em dia por aqui.
            </Card>
          )}
          {gruposRepasse.map((grupo) => (
            <Card key={grupo.fornecedor_id} className="overflow-hidden">
              <div className="p-4 flex items-center justify-between">
                <button
                  className="flex items-center gap-3 text-left flex-1"
                  onClick={() => alternarGrupo(grupo.fornecedor_id)}
                >
                  <Store className="w-5 h-5 text-gray-400 shrink-0" />
                  <div>
                    <p className="font-semibold text-white">{grupo.fornecedor_nome}</p>
                    <p className="text-sm text-gray-400">
                      {grupo.quantidade_pedidos} pedido(s) entregue(s), aguardando repasse
                    </p>
                  </div>
                  {gruposExpandidos[grupo.fornecedor_id] ? (
                    <ChevronUp className="w-4 h-4 ml-2" />
                  ) : (
                    <ChevronDown className="w-4 h-4 ml-2" />
                  )}
                </button>
                <div className="flex items-center gap-4">
                  <p className="text-lg font-bold text-emerald-700">{formatarMoeda(grupo.valor_total)}</p>
                  <Button
                    onClick={() => abrirConfirmacaoRepasse(grupo)}
                    disabled={processandoId === grupo.fornecedor_id}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {processandoId === grupo.fornecedor_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Autorizar repasse
                  </Button>
                </div>
              </div>

              {gruposExpandidos[grupo.fornecedor_id] && (
                <div className="border-t bg-muted/30 px-4 py-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400">
                        <th className="py-2">Pedido</th>
                        <th className="py-2">Produto</th>
                        <th className="py-2">Marketplace</th>
                        <th className="py-2">Entregue em</th>
                        <th className="py-2 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.pedidos.map((p) => (
                        <tr key={p.id} className="border-t border-muted">
                          <td className="py-2 font-mono text-xs">{p.codigo}</td>
                          <td className="py-2">{p.produto_nome}</td>
                          <td className="py-2 capitalize">{p.marketplace || "-"}</td>
                          <td className="py-2">{formatarData(p.entregue_em)}</td>
                          <td className="py-2 text-right">{formatarMoeda(p.valor_fornecedor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* ABA: Aguardando entrega */}
      {!carregando && abaAtiva === "aguardando" && (
        <div className="space-y-3">
          {pedidosAguardando.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum pedido aguardando confirmação de entrega.
            </Card>
          )}
          {pedidosAguardando.map((pedido) => (
            <Card key={pedido.id} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-white">
                  {pedido.codigo} — {pedido.produto_nome}
                </p>
                <p className="text-sm text-gray-400">
                  {pedido.cliente_nome} · {pedido.marketplace || "marketplace não informado"} ·{" "}
                  vendido em {formatarData(pedido.created_at)}
                </p>
                <p className="text-sm text-gray-400">
                  Garantia reservada: {formatarMoeda(pedido.valor_distribuidora)}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => abrirCancelamento(pedido)}
                  disabled={processandoId === pedido.id}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={() => confirmarEntrega(pedido.id)}
                  disabled={processandoId === pedido.id}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {processandoId === pedido.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Truck className="w-4 h-4 mr-2" />
                  )}
                  Confirmar entrega
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ABA: Histórico */}
      {!carregando && abaAtiva === "historico" && (
        <div className="space-y-2">
          {pedidosHistorico.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">Nenhum pedido no histórico recente.</Card>
          )}
          {pedidosHistorico.map((pedido) => (
            <Card key={pedido.id} className="p-3 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3">
                {pedido.status === "cancelado" ? (
                  <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                )}
                <div>
                  <p className="font-medium">
                    {pedido.codigo} — {pedido.produto_nome}
                  </p>
                  <p className="text-gray-400">
                    {pedido.status === "cancelado"
                      ? `Cancelado: ${pedido.motivo_cancelamento || "sem motivo informado"}`
                      : pedido.repasse_status === "autorizado"
                      ? "Repasse autorizado"
                      : "Entregue, aguardando repasse"}
                  </p>
                </div>
              </div>
              <p className="text-gray-400">{formatarData(pedido.updated_at)}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de cancelamento */}
      {modalCancelar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <div className="flex items-center gap-2 mb-4 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-semibold text-white">Cancelar pedido {modalCancelar.codigo}</h3>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              O fornecedor será avisado para não enviar o produto, e a garantia reservada volta para o
              saldo disponível do afiliado.
            </p>
            <textarea
              className="w-full border border-gray-700 bg-gray-800 text-white rounded-md p-2 text-sm mb-4"
              rows={3}
              placeholder="Motivo do cancelamento (ex: cliente desistiu da compra)"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalCancelar(null)}>
                Voltar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmarCancelamento}
                disabled={processandoId === modalCancelar.id}
              >
                {processandoId === modalCancelar.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Confirmar cancelamento
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de confirmação de repasse - ação financeira, exige
          confirmação explícita antes de executar (achado da sessão de
          deploy: antes o botão executava direto, sem nenhuma chance de
          voltar atrás) */}
      {modalRepasse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <div className="flex items-center gap-2 mb-4 text-emerald-600">
              <DollarSign className="w-5 h-5" />
              <h3 className="font-semibold text-white">Autorizar repasse para {modalRepasse.fornecedor_nome}</h3>
            </div>
            <p className="text-sm text-gray-400 mb-2">
              Você está prestes a autorizar o repasse de:
            </p>
            <p className="text-2xl font-bold text-emerald-400 mb-4">
              {formatarMoeda(modalRepasse.valor_total)}
            </p>
            <p className="text-sm text-gray-400 mb-4">
              Isso libera a garantia reservada de {modalRepasse.quantidade_pedidos} pedido(s) da carteira
              do(s) afiliado(s) diretamente para o fornecedor. Essa ação não pode ser desfeita por aqui.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalRepasse(null)}>
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={confirmarAutorizacaoRepasse}
                disabled={processandoId === modalRepasse.fornecedor_id}
              >
                {processandoId === modalRepasse.fornecedor_id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Sim, autorizar repasse
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RepassesAdmin;
